// ABOUTME: Resolves which theme is active — returns undefined (not-yet-known) until resolved, no flash.
// ABOUTME: Unauthenticated pages use the resolved site default; logged-in users use their family's theme.
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from './useSiteSettings';
import { useImpersonation } from './useImpersonation';
import { familyAPI } from '../services/api';
import { isValidThemeName, type ThemeName } from '../theme/themeRegistry';

export function useEffectiveTheme(): ThemeName | undefined {
  const { user } = useAuth();
  const { data: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  const { impersonatedUser, isLoading: impersonationLoading } = useImpersonation();
  const [familyThemeName, setFamilyThemeName] = useState<ThemeName | undefined>(undefined);

  // Only logged-in users need to fetch anything — unauthenticated resolution is a pure
  // derived value below, computed on every render from siteSettings (no state/effect needed).
  useEffect(() => {
    if (!user || impersonationLoading) return;

    let cancelled = false;
    const resolveFamilyTheme = async () => {
      // Reset to undefined (blocking) on every re-fetch — e.g. when impersonation changes —
      // so a stale theme from the previous family/user never renders during the refetch window.
      setFamilyThemeName(undefined);

      try {
        const families = await familyAPI.getFamilies();
        if (families.length > 0) {
          const details = await familyAPI.getFamilyDetails(families[0].familyId);
          if (!cancelled) {
            setFamilyThemeName(isValidThemeName(details.effectiveThemeName) ? details.effectiveThemeName : 'nalu-v1');
          }
        } else if (!cancelled) {
          setFamilyThemeName('nalu-v1');
        }
      } catch (error) {
        console.error('Failed to fetch family theme:', error);
        if (!cancelled) {
          setFamilyThemeName('nalu-v1');
        }
      }
    };

    resolveFamilyTheme();
    return () => { cancelled = true; };
  }, [user, impersonatedUser?.userId, impersonationLoading]);

  if (!user) {
    if (siteSettingsLoading) return undefined;
    return isValidThemeName(siteSettings?.defaultThemeName) ? siteSettings!.defaultThemeName : 'nalu-v1';
  }

  if (impersonationLoading) return undefined;

  return familyThemeName;
}
